// Guía de despacho (docs/108 B7). La GUÍA ELECTRÓNICA 52 está BLOQUEADA a propósito (D-127, Res.
// 154/2025: falta la decisión tributaria sobre los datos del traslado) y aquí se dice. Lo que sí opera:
// la consulta de los despachos de venta (cada salida física confirmada) y su COMPROBANTE INTERNO de
// despacho impreso, que no es un documento tributario.
import { DocumentosNota } from '../dte/DocumentosNota';
import { useDocumentosEmitidos } from '../dte/documentosEmitidos';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import type { Despacho } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { fecha } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useBodegas } from '../stock/PantallasStock';
import { GuiaInternaDe } from './GuiaInterna';

export function DespachosVenta({
  alAbrirNota,
}: {
  alAbrirNota: (notaId: string, numero: string) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const [estado, setEstado] = useState('CONFIRMADO');
  const [imprimir, setImprimir] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'despachos-venta', estado],
    queryFn: () =>
      api.get<{ items: Despacho[] }>('/ventas/despachos', {
        query: { empresaId, estado: estado || undefined },
      }),
  });
  const bodega = (id: string) => bodegas.lista.find((b) => b.id === id)?.nombre ?? '';
  // El documento tributario de cada despacho (2026-10-26): la factura o boleta, no la nota.
  const notaIds = [...new Set((q.data?.items ?? []).map((d) => d.nota_id))];
  const docs = useDocumentosEmitidos(
    empresaId,
    { notaIds: notaIds.slice(0, 200).join(',') },
    notaIds.length > 0,
  );
  const docsDeNota = (notaId: string) =>
    (docs.data?.items ?? []).filter(
      (x) => x.origen?.notaId === notaId && [33, 34, 39, 41].includes(x.tipoDte),
    );
  const columnas: Columna<Despacho>[] = [
    {
      id: 'n',
      titulo: 'Despacho',
      celda: (d) => <span className="mono">{`DES-${d.numero}`}</span>,
      ancho: '8rem',
    },
    { id: 'f', titulo: 'Fecha', celda: (d) => fecha(d.fecha_negocio), ancho: '7rem' },
    {
      id: 'doc',
      titulo: 'Documento',
      celda: (d) => <DocumentosNota documentos={docsDeNota(d.nota_id)} vacio="sin documento" />,
      ancho: '11rem',
    },
    {
      id: 'm',
      titulo: 'Modalidad',
      celda: (d) => d.modalidad.toLowerCase().replace(/_/g, ' '),
      ancho: '9rem',
    },
    { id: 'b', titulo: 'Desde', celda: (d) => bodega(d.ubicacion_id), ancho: '12rem' },
    { id: 'e', titulo: 'Estado', celda: (d) => d.estado.toLowerCase(), ancho: '8rem' },
    {
      id: 'acc',
      titulo: '',
      ancho: '14rem',
      celda: (d) => (
        <span className="acciones-fila">
          <button
            type="button"
            className="boton-mini"
            onClick={() => alAbrirNota(d.nota_id, 'Nota')}
          >
            Ver nota
          </button>
          {d.estado === 'CONFIRMADO' && (
            <button type="button" className="boton-mini" onClick={() => setImprimir(d.id)}>
              Guía interna
            </button>
          )}
        </span>
      ),
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="despachos-venta">
      <div className="sin-fuente" role="note">
        <b>GUÍA INTERNA (GDI), COMO EN RANDOM</b>
        <span>
          Mientras la guía electrónica 52 no se certifique (D-127), cada despacho sale con una guía
          interna impresa con folio propio (no tributaria, no va al SII).
        </span>
      </div>
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado del despacho"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="BORRADOR">Borrador</option>
            <option value="CONFIRMADO">Confirmado</option>
            <option value="DESCARTADO">Descartado</option>
          </select>
        </label>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Despachos de venta"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(d) => d.id}
        cargando={q.isLoading}
        vacio="Sin despachos"
      />
      {imprimir && (
        <GuiaInternaDe origen={{ despachoId: imprimir }} alTerminar={() => setImprimir(null)} />
      )}
    </div>
  );
}
